import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  checkSession,
  validateToken,
  logout,
  getProfile,
  cancelSubscription,
  getPlans,
  createSubscriptionCheckout,
  purchaseTunnelPack,
  verifyPayment,
  deletePayment,
  retryPayment,
  addSeat,
  updateSeat,
  removeSeat,
  getPartnerSummary,
} from '../api';
import LanguageSwitcher from '../components/LanguageSwitcher';

// Fallback pack pricing — overridden by the plans endpoint when it returns pack info.
const DEFAULT_TUNNEL_PACKS = [
  { size: 10, price_usd_cents: 800 },
  { size: 50, price_usd_cents: 2700 },
  { size: 100, price_usd_cents: 4600 },
];

function Profile() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [error, setError] = useState(null);
  const [profile, setProfile] = useState(null);
  const [plans, setPlans] = useState(null);
  const [partner, setPartner] = useState(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [showAddSeat, setShowAddSeat] = useState(false);
  const [seatPhone, setSeatPhone] = useState('');
  const [seatPlan, setSeatPlan] = useState('pro');

  // Check for token in URL or existing session
  const initAuth = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const token = searchParams.get('token');

      if (token) {
        // Validate magic link token
        const result = await validateToken(token);
        if (result.authenticated) {
          setAuthenticated(true);
          // Remove token from URL
          window.history.replaceState({}, '', '/profile');
        }
      } else {
        // Check existing session
        const result = await checkSession();
        setAuthenticated(result.authenticated);
      }
    } catch (err) {
      setError(err.message);
      setAuthenticated(false);
    } finally {
      setLoading(false);
    }
  }, [searchParams]);

  // Load profile data
  const loadProfile = useCallback(async () => {
    if (!authenticated) return;

    try {
      const [profileData, plansData, partnerData] = await Promise.all([
        getProfile(),
        getPlans(),
        getPartnerSummary().catch(() => null),
      ]);
      setProfile(profileData);
      setPlans(plansData);
      setPartner(partnerData);
    } catch (err) {
      setError(err.message);
    }
  }, [authenticated]);

  useEffect(() => {
    initAuth();
  }, [initAuth]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  // Check for payment result and verify with backend
  useEffect(() => {
    const payment = searchParams.get('payment');
    const txnId = searchParams.get('txnId');

    if (payment === 'success' && txnId) {
      // Poll payment status with exponential backoff until resolved
      let cancelled = false;
      const verifyPaymentStatus = async () => {
        const MAX_ATTEMPTS = 8;
        const BASE_DELAY = 2000;
        let finalStatus = 'success'; // Assume success unless verification says otherwise

        for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
          if (cancelled) return;

          const delay = Math.min(BASE_DELAY + attempt * 2000, 10000);
          await new Promise(resolve => setTimeout(resolve, delay));

          try {
            const result = await verifyPayment(txnId);
            if (result.status !== 'pending' && result.status !== 'processing') {
              finalStatus = result.status === 'completed' ? 'success' : 'failed';
              break;
            }
          } catch (err) {
            console.error(`Payment verification attempt ${attempt + 1} failed:`, err);
          }
        }

        // Reload profile and set URL to reflect actual payment result
        if (!cancelled) {
          await loadProfile();
          window.history.replaceState({}, '', `/profile?payment=${finalStatus}`);
        }
      };
      verifyPaymentStatus();
      return () => { cancelled = true; };
    } else if (payment === 'success') {
      // No txnId, just reload profile
      loadProfile();
    }
  }, [searchParams, loadProfile]);

  const handleLogout = async () => {
    try {
      await logout();
      setAuthenticated(false);
      setProfile(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm(t('profile.subscription.cancelConfirm'))) {
      return;
    }

    setActionLoading(true);
    try {
      await cancelSubscription();
      await loadProfile(); // Reload to show updated status
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpgrade = async (plan) => {
    setActionLoading(true);
    try {
      const result = await createSubscriptionCheckout(plan);
      if (!result?.checkout_url) { setError(t('profile.subscription.checkoutFailed')); setActionLoading(false); return; }
      window.location.href = result.checkout_url;
    } catch (err) {
      setError(err.message);
      setActionLoading(false);
    }
  };

  const handleBuyPack = async (packSize) => {
    setActionLoading(true);
    try {
      const result = await purchaseTunnelPack(packSize);
      if (!result?.checkout_url) { setError(t('profile.packs.checkoutFailed')); setActionLoading(false); return; }
      window.location.href = result.checkout_url;
    } catch (err) {
      setError(err.message);
      setActionLoading(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="profile-page">
        <div className="container">
          <div className="loading">{t('profile.loading')}</div>
        </div>
      </div>
    );
  }

  // Not authenticated
  if (!authenticated) {
    return (
      <div className="profile-page">
        <div className="container">
          <div className="auth-required">
            <h1>{t('profile.accessTitle')}</h1>
            <p>{t('profile.accessBody')}</p>
            {error && <p className="error">{error}</p>}
            <Link to="/" className="btn-primary">{t('profile.backToHome')}</Link>
          </div>
        </div>
      </div>
    );
  }

  // Payment result messages
  const paymentStatus = searchParams.get('payment');
  const paidPlans = plans?.plans
    ? Object.entries(plans.plans).filter(([planKey]) => planKey !== 'free')
    : [];

  // Prefer pack pricing from the plans endpoint when present, fall back to defaults.
  const tunnelPacks = DEFAULT_TUNNEL_PACKS.map((pack) => ({
    ...pack,
    price_usd_cents:
      plans?.tunnel_packs?.[pack.size]?.price_usd_cents ?? pack.price_usd_cents,
  }));

  // Helpers for partner next-tier rate display
  const nextTierRate = (threshold) => {
    if (threshold >= 150) return '40%';
    if (threshold >= 50) return '30%';
    return '20%';
  };

  return (
    <div className="profile-page">
      <div className="container">
        {/* Header */}
        <header className="profile-header">
          <div className="profile-brand">
            <img src="/logo192.png" alt="10 Thirty" className="profile-logo" />
            <h1>{t('profile.header')}</h1>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <LanguageSwitcher />
            <button onClick={handleLogout} className="btn-secondary btn-small">
              {t('profile.logOut')}
            </button>
          </div>
        </header>

        {/* Payment Status */}
        {paymentStatus === 'success' && (
          <div className="alert alert-success">
            {t('profile.alerts.paymentSuccess')}
          </div>
        )}
        {paymentStatus === 'failed' && (
          <div className="alert alert-error">
            {t('profile.alerts.paymentFailed')}
          </div>
        )}
        {paymentStatus === 'cancelled' && (
          <div className="alert alert-warning">
            {t('profile.alerts.paymentCancelled')}
          </div>
        )}

        {/* Error */}
        {error && <div className="alert alert-error">{error}</div>}

        {profile && (
          <>
            {/* User Info */}
            <section className="profile-section">
              <h2>{t('profile.info.heading')}</h2>
              <div className="profile-card">
                <div className="profile-info">
                  <span className="label">{t('profile.info.phoneLabel')}</span>
                  <span className="value">{profile.user.phone}</span>
                </div>
                {profile.user.name && (
                  <div className="profile-info">
                    <span className="label">{t('profile.info.nameLabel')}</span>
                    <span className="value">{profile.user.name}</span>
                  </div>
                )}
              </div>
            </section>

            {/* Subscription */}
            <section className="profile-section">
              <h2>{t('profile.subscription.heading')}</h2>
              <div className="profile-card subscription-card">
                <div className="subscription-header">
                  <div className="plan-badge" data-plan={profile.subscription.plan}>
                    {profile.subscription.plan_name}
                  </div>
                  {profile.subscription.cancelled_at && (
                    <span className="status-badge cancelled">{t('profile.subscription.cancelling')}</span>
                  )}
                </div>

                {profile.subscription.plan !== 'free' && (
                  <>
                    <div className="profile-info">
                      <span className="label">{t('profile.subscription.amountLabel')}</span>
                      <span className="value">
                        {profile.subscription.amount_usd_cents
                          ? t('profile.subscription.perMonthUsd', { amount: (profile.subscription.amount_usd_cents / 100).toFixed(0) })
                          : t('profile.subscription.perMonthZar', { amount: (profile.subscription.amount_cents / 100).toFixed(0) })}
                      </span>
                    </div>
                    {profile.subscription.current_period_end && (
                      <div className="profile-info">
                        <span className="label">
                          {profile.subscription.cancelled_at
                            ? t('profile.subscription.accessUntilLabel')
                            : t('profile.subscription.nextBillingLabel')}
                        </span>
                        <span className="value">
                          {new Date(profile.subscription.current_period_end).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </>
                )}

                <div className="subscription-actions">
                  {profile.subscription.plan === 'free' && (
                    <button
                      onClick={() => setShowUpgrade(true)}
                      className="btn-primary"
                      disabled={actionLoading}
                    >
                      {t('profile.subscription.choosePlan')}
                    </button>
                  )}
                  {profile.subscription.plan !== 'free' && profile.subscription.status === 'active' && !profile.subscription.cancelled_at && (
                    <button
                      onClick={handleCancelSubscription}
                      className="btn-secondary"
                      disabled={actionLoading}
                    >
                      {t('profile.subscription.cancelSubscription')}
                    </button>
                  )}
                </div>
              </div>
            </section>

            {/* Partner Program */}
            {partner && partner.is_partner && (
              <section className="profile-section">
                <h2>{t('profile.partner.heading')}</h2>
                <div className="profile-card partner-card">
                  <div className="partner-header">
                    <div className={`tier-badge tier-${partner.tier}`}>
                      <span className="tier-name">{partner.tier}</span>
                      <span className="tier-rate">{t('profile.partner.ratePercent', { percent: (partner.rate_bps / 100).toFixed(0) })}</span>
                    </div>
                    <div className="partner-meta">
                      <div className="partner-meta-label">{t('profile.partner.currentRate')}</div>
                      <div className="partner-meta-sub">
                        {t('profile.partner.stamped')}
                      </div>
                    </div>
                  </div>

                  <div className="partner-stats">
                    <div className="partner-stat">
                      <span className="partner-stat-value">{partner.active_paying_referrals}</span>
                      <span className="partner-stat-label">{t('profile.partner.activeReferrals')}</span>
                    </div>
                    <div className="partner-stat">
                      <span className="partner-stat-value">
                        {partner.total_commission_usd_cents
                          ? `$${(partner.total_commission_usd_cents / 100).toFixed(2)}`
                          : `R${(partner.total_commission_cents / 100).toFixed(2)}`}
                      </span>
                      <span className="partner-stat-label">{t('profile.partner.lifetimeEarned')}</span>
                    </div>
                    <div className="partner-stat">
                      <span className="partner-stat-value">
                        R{(partner.pending_commission_cents / 100).toFixed(2)}
                      </span>
                      <span className="partner-stat-label">{t('profile.partner.pendingPayout')}</span>
                    </div>
                    <div className="partner-stat">
                      <span className="partner-stat-value">
                        R{(partner.paid_commission_cents / 100).toFixed(2)}
                      </span>
                      <span className="partner-stat-label">{t('profile.partner.paidOut')}</span>
                    </div>
                  </div>

                  {partner.next_tier_threshold !== null && (
                    <div className="partner-next-tier">
                      {t('profile.partner.nextTier', {
                        count: partner.next_tier_in,
                        threshold: partner.next_tier_threshold,
                        rate: nextTierRate(partner.next_tier_threshold),
                      })}
                    </div>
                  )}

                  <p className="partner-share">
                    {t('profile.partner.share', {
                      code: profile.user.referral_code || t('profile.partner.shareCodeFallback'),
                      percent: (partner.rate_bps / 100).toFixed(0),
                    })}
                  </p>
                </div>
              </section>
            )}

            {/* Business Team Seats */}
            {profile.seats && (
              <section className="profile-section">
                <h2>{t('profile.seats.heading')}</h2>
                <div className="profile-card">
                  <p className="tunnel-packs-desc">
                    {t('profile.seats.desc')}
                  </p>

                  {profile.seats.seats.length > 0 && (
                    <div className="seats-list">
                      {profile.seats.seats.map((seat) => (
                        <div key={seat.id} className="seat-item">
                          <div className="seat-info">
                            <span className="seat-name">
                              {seat.status === 'pending_invite'
                                ? `${seat.invite_phone}${t('profile.seats.pendingSuffix')}`
                                : seat.member?.name || seat.member?.phone || t('profile.seats.unknownMember')}
                            </span>
                            <span className="seat-plan">
                              <select
                                value={seat.assigned_plan}
                                onChange={async (e) => {
                                  try {
                                    await updateSeat(seat.id, e.target.value);
                                    await loadProfile();
                                  } catch (err) {
                                    setError(err.message);
                                  }
                                }}
                                className="seat-plan-select"
                              >
                                <option value="pro">{t('profile.seats.planPro')}</option>
                                <option value="business">{t('profile.seats.planBusiness')}</option>
                              </select>
                            </span>
                          </div>
                          <button
                            className="btn-danger btn-small"
                            disabled={actionLoading}
                            onClick={async () => {
                              const name = seat.member?.name || seat.invite_phone || t('profile.seats.removeFallbackName');
                              if (!window.confirm(t('profile.seats.removeConfirm', { name }))) return;
                              setActionLoading(true);
                              try {
                                await removeSeat(seat.id);
                                await loadProfile();
                              } catch (err) {
                                setError(err.message);
                              } finally {
                                setActionLoading(false);
                              }
                            }}
                          >
                            {t('profile.seats.remove')}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {profile.seats.seat_count > 0 && (
                    <div className="seat-billing">
                      <span>{t('profile.seats.seatCount', { count: profile.seats.seat_count })}</span>
                      <span className="seat-total">{t('profile.seats.seatTotal', { amount: (profile.seats.total_usd_cents / 100).toFixed(0) })}</span>
                    </div>
                  )}

                  {showAddSeat ? (
                    <div className="add-seat-form">
                      <input
                        type="tel"
                        value={seatPhone}
                        onChange={(e) => setSeatPhone(e.target.value)}
                        placeholder={t('profile.seats.phonePlaceholder')}
                        className="seat-input"
                      />
                      <select
                        value={seatPlan}
                        onChange={(e) => setSeatPlan(e.target.value)}
                        className="seat-plan-select"
                      >
                        <option value="pro">{t('profile.seats.planPro')}</option>
                        <option value="business">{t('profile.seats.planBusiness')}</option>
                      </select>
                      <div className="add-seat-actions">
                        <button
                          className="btn-primary btn-small"
                          disabled={actionLoading || !seatPhone.trim()}
                          onClick={async () => {
                            setActionLoading(true);
                            try {
                              await addSeat(seatPhone.trim(), seatPlan);
                              setSeatPhone('');
                              setShowAddSeat(false);
                              await loadProfile();
                            } catch (err) {
                              setError(err.message);
                            } finally {
                              setActionLoading(false);
                            }
                          }}
                        >
                          {actionLoading ? t('profile.seats.addMemberSubmitting') : t('profile.seats.addMemberSubmit')}
                        </button>
                        <button
                          className="btn-secondary btn-small"
                          onClick={() => { setShowAddSeat(false); setSeatPhone(''); }}
                        >
                          {t('profile.seats.cancel')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      className="btn-primary"
                      onClick={() => setShowAddSeat(true)}
                      disabled={actionLoading}
                    >
                      {t('profile.seats.addMember')}
                    </button>
                  )}
                </div>
              </section>
            )}

            {/* Tunnel Credits */}
            <section className="profile-section">
              <h2>{t('profile.credits.heading')}</h2>
              <div className="profile-card">
                <div className="credits-grid">
                  <div className="credit-item">
                    <span className="credit-value">{profile.credits.credits_remaining}</span>
                    <span className="credit-label">{t('profile.credits.packCredits')}</span>
                  </div>
                  <div className="credit-item">
                    <span className="credit-value">
                      {profile.credits.daily_tunnels === -1
                        ? t('profile.credits.unlimited')
                        : `${profile.credits.tunnels_used_today}/${profile.credits.daily_tunnels}`}
                    </span>
                    <span className="credit-label">{t('profile.credits.dailyTunnels')}</span>
                  </div>
                </div>

                {/* Buy Tunnel Packs */}
                <div className="tunnel-packs">
                  <h4>{t('profile.packs.heading')}</h4>
                  <p className="tunnel-packs-desc">{t('profile.packs.desc')}</p>
                  <div className="packs-grid">
                    {tunnelPacks.map((pack) => (
                      <button
                        key={pack.size}
                        className="pack-btn"
                        disabled={actionLoading}
                        onClick={() => handleBuyPack(pack.size)}
                      >
                        <span className="pack-size">{pack.size}</span>
                        <span className="pack-label">{t('profile.packs.tunnels')}</span>
                        <span className="pack-price">${(pack.price_usd_cents / 100).toFixed(0)}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* Payment History */}
            {profile.recent_payments.length > 0 && (
              <section className="profile-section">
                <h2>{t('profile.payments.heading')}</h2>
                <div className="payments-list">
                  {profile.recent_payments.map((payment) => (
                    <div key={payment.id} className="payment-item">
                      <div className="payment-info">
                        <span className="payment-desc">{payment.description}</span>
                        <span className="payment-date">
                          {new Date(payment.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="payment-amount">
                        <span className={`payment-status ${payment.status}`}>
                          {t(`profile.payments.status${payment.status.charAt(0).toUpperCase() + payment.status.slice(1)}`, payment.status)}
                        </span>
                        <span className="payment-value">
                          {payment.amount_usd_cents
                            ? `$${(payment.amount_usd_cents / 100).toFixed(0)}`
                            : `R${(payment.amount_cents / 100).toFixed(0)}`}
                        </span>
                      </div>
                      {(payment.status === 'pending' || payment.status === 'failed') && (
                        <div className="payment-actions">
                          <button
                            className="btn-primary btn-small"
                            disabled={actionLoading}
                            onClick={async () => {
                              setActionLoading(true);
                              try {
                                const result = await retryPayment(payment.id);
                                window.location.href = result.checkout_url;
                              } catch (err) {
                                setError(err.message);
                                setActionLoading(false);
                              }
                            }}
                          >
                            {t('profile.payments.completePayment')}
                          </button>
                          <button
                            className="btn-danger btn-small"
                            disabled={actionLoading}
                            onClick={async () => {
                              if (!window.confirm(t('profile.payments.deleteConfirm'))) return;
                              setActionLoading(true);
                              try {
                                await deletePayment(payment.id);
                                await loadProfile();
                              } catch (err) {
                                setError(err.message);
                              } finally {
                                setActionLoading(false);
                              }
                            }}
                          >
                            {t('profile.payments.delete')}
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Return to App */}
            <section className="profile-section">
              <a href="tenthirtyapp://" className="btn-primary btn-full">
                {t('profile.returnToApp')}
              </a>
            </section>
          </>
        )}

        {/* Upgrade Modal */}
        {showUpgrade && plans && (
          <div className="modal-overlay" onClick={() => setShowUpgrade(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <h2>{t('profile.upgrade.heading')}</h2>
              <p className="modal-subtitle">{t('profile.upgrade.subtitle')}</p>
              <div className="plans-grid">
                {paidPlans.map(([planKey, plan]) => (
                  <div
                    key={planKey}
                    className={`plan-option ${planKey === 'pro' ? 'featured' : 'business-option'}`}
                  >
                    <h3>{plan.name}</h3>
                    <div className="plan-price">{plan.price_formatted || t('profile.upgrade.perMonth', { amount: plan.price_usd_cents / 100 })}</div>
                    <ul>
                      <li>{plan.daily_tunnels === -1 ? t('profile.upgrade.unlimitedTunnels') : t('profile.upgrade.tunnelsPerDay', { count: plan.daily_tunnels })}</li>
                      <li>{plan.max_contacts === -1 ? t('profile.upgrade.unlimitedContacts') : t('profile.upgrade.contactsLimit', { count: plan.max_contacts })}</li>
                      <li>{t('profile.upgrade.minuteTunnels', { count: Math.floor(plan.max_tunnel_duration / 60) })}</li>
                      <li>
                        {plan.max_message_ttl >= 60
                          ? t('profile.upgrade.minuteExpiry', { count: Math.floor(plan.max_message_ttl / 60) })
                          : t('profile.upgrade.secondExpiry', { count: plan.max_message_ttl })}
                      </li>
                    </ul>
                    <button
                      onClick={() => handleUpgrade(planKey)}
                      className="btn-primary"
                      disabled={actionLoading}
                    >
                      {actionLoading ? t('profile.upgrade.loadingButton') : t('profile.upgrade.choose', { name: plan.name })}
                    </button>
                  </div>
                ))}
              </div>
              <button onClick={() => setShowUpgrade(false)} className="btn-secondary modal-close">
                {t('profile.upgrade.cancel')}
              </button>
            </div>
          </div>
        )}

        {/* Footer */}
        <footer className="profile-footer">
          <div className="links">
            <Link to="/">{t('profile.footer.home')}</Link>
            <Link to="/privacy">{t('profile.footer.privacy')}</Link>
            <Link to="/terms">{t('profile.footer.terms')}</Link>
          </div>
          <p>
            {t('landing.footer.copyright', { year: new Date().getFullYear() })}{' '}
            {t('landing.footer.broughtToYou')}{' '}
            <a href="https://www.callitservices.co.za" target="_blank" rel="noopener noreferrer">{t('landing.footer.callItServices')}</a>
          </p>
        </footer>
      </div>
    </div>
  );
}

export default Profile;
